/**
 * The sheet's what-if: where one column's number goes over the phase, and what the client's own
 * result does to it.
 *
 * ── It is a SIMULATION, not the plan ─────────────────────────────────────────────────────────
 * The matrix says what this plan prescribes. This says what the RULE does, given a client who
 * delivers and a client who does not — the two scenarios behind the mock's „Klient: dowozi / nie
 * dowozi" segment. On a TEMPLATE nothing here is a fact about anybody: there is no client and no
 * logged result, so the whole thing is a drawing of the rule the trainer is authoring.
 *
 * Ported from the owner's mock (`Import projektu 1_1 (1)/Builder planu - modal - progresja.dc.html`,
 * the `sim` loop) with ONE deliberate correction, which the plan records as a defect in the mock
 * rather than a choice here:
 *
 * **THE ROUNDING IS THE BACKEND'S, NOT THE MOCK'S.** The mock rounds every step to two decimals and
 * its own explainer describes the IN-SESSION plate policy ("krok 2,5 kg, poniżej 30 kg — 1,25").
 * That policy is real but belongs to workout start (R5); an authoring-time expansion lands on the
 * FIELD's progression grid instead, which for the load is a flat 1,25 kg and for reps is whole
 * numbers from one. `advanceFieldValue` is the same function the matrix projects through and the
 * same arithmetic `progression-projection-parity.json` pins, so the chart and the grid can never
 * show two different numbers for one rule.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  advanceFieldValue,
  advanceOnAxis,
  availableAxisFields,
  axisBaseOf,
  axisForField,
  bandedGateOf,
  countsAsWork,
  measurementValueKey,
  projectionRuleFor,
  ruleFor,
  snapToAxis,
  snapToProgressionGrid,
  type ExerciseDraft,
  type ProgressionAxis,
  type ProgressionAxisKind,
  type ProgressionRule,
  type ProgressionRules,
  type ProjectionRule,
} from '~/entities/plan-draft';
import { measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';

import {
  loggedCellKey,
  type CycleVerdict,
  type LiveMatrixInput,
  type LoggedCell,
} from './executionsToMatrixInputs';
import { absenceKindOf, bandColumnOf, formatNumber, setRowText } from './matrixCellState';
import { bandCountText, gateSubjectKey, sentenceBand, sentenceBase } from './ruleSentence';

/** Whether the what-if assumes the client clears the bar or falls short of it. */
export type TrajectoryScenario = 'delivers' | 'fallsShort';

/** The bar the rule places on the gated column. `sentenceBand`'s own answer, carried through. */
export interface TrajectoryBand {
  readonly floor: number;
  readonly ceiling: number;
}

/**
 * HOW a client fell short — the mock's three, and they are three different sentences about the same
 * verdict. The engine does not distinguish them (a cycle either earned its jump or did not), so
 * these are a drawing of what falling short LOOKS like rather than a state the plan holds.
 */
export type ShortfallKind = 'fewerReps' | 'moreLoadFewerReps' | 'lessLoadMoreReps';

/**
 * The mock's own order, keyed by position: the second cycle shows fewer repetitions, the third more
 * load at fewer repetitions, the fourth less load at more. Positional because the mock is an
 * illustration — nothing in the plan says which kind a given cycle would see.
 *
 * IT RUNS OUT, and that is the mock's own `CASES[c] || null` rather than an oversight here. The three
 * are a DEMONSTRATION of three ways to fall short, which is what the mock's narration calls them;
 * every cycle past them is delivered. It read modulo three until 15 September 2026, when the owner
 * asked for the plot to be the mock's in behaviour as well as in pixels — and the fifth column of
 * his own capture is the delivered one. The consequence on a phase longer than four cycles is
 * visible and deliberate: „nie dowozi" demonstrates the three and the run then carries on.
 */
const SHORTFALL_BY_OFFSET: readonly ShortfallKind[] = [
  'fewerReps',
  'moreLoadFewerReps',
  'lessLoadMoreReps',
];

/** One cycle of the run. */
export interface TrajectoryPoint {
  /** 1-based position in the phase, matching the matrix's own column labels. */
  readonly cycle: number;
  /** The stepped column's value in this cycle, on the field's own progression grid. */
  readonly value: number;
  /**
   * Where the client stands inside the band in this cycle, or null when the rule places no bar.
   * `climbed >= ceiling` is the cycle that earns the jump.
   */
  readonly climbed: number | null;
  /**
   * Whether this cycle was delivered. `null` on the base cycle, which is authored rather than
   * earned, and on every cycle of a rule that does not read the client's result at all.
   */
  readonly delivered: boolean | null;
  /** True where the value moved because the previous cycle was delivered. */
  readonly jumped: boolean;
  /** How the client fell short here, or null where they did not. */
  readonly shortfall: ShortfallKind | null;
  /** What they actually did on the gated column under a shortfall — a drawing, never a fact. */
  readonly shortfallLoad: number | null;
  /**
   * What this cycle ASKS on the paired column — the band's climb where there is a band, the row's own
   * prescription where there is not, and null where the row prescribes nothing but the stepped column.
   *
   * The label over a node is „24 kg × 10–12" rather than „24 kg", and the second half of it is this.
   * The mock prints the pair on every labelled node; without it the plot states a load and says
   * nothing about the work it is a load FOR.
   */
  readonly gateAsked: number | null;
  /** The bar still above `gateAsked`, or null on a cycle standing at it — „× 12", never „× 12–12". */
  readonly gateCeiling: number | null;
  /**
   * What the simulated client DID on that column. Null on a cycle nobody was asked to deliver — the
   * base cycle, and every cycle of a rule that does not read the client's result at all.
   */
  readonly gateDone: number | null;
  /**
   * Whether this point is a session the client actually LIVED, rather than a cycle the rule is being
   * simulated over.
   *
   * It is the one distinction the owner asked for by name (15 September 2026): the plot must tell
   * real execution apart from the what-if. Everything it changes is downstream — the caption's word,
   * the line's dash and the node's fill — and nothing upstream, because a lived cycle's numbers are
   * the record's and a simulated one's are the engine's.
   */
  readonly isReal: boolean;
  /**
   * Whether the ENGINE is answering for this cycle, the what-if the scenario segment steers. False on
   * a lived cycle and on a past cycle the record holds nothing for (a session before the plan start):
   * the second is neither a session nor a prediction, so it states the plain prescription, carries
   * no verdict, and belongs to the solid run rather than the dashed one.
   */
  readonly isSimulated: boolean;
  /**
   * The backend's reading of a cycle that HAPPENED, or null on every simulated one.
   *
   * `delivered` cannot carry it: it has three values and the record has six, and the three that mean
   * nobody trained are neither a yes nor a no. It is what the caption reads to print „bez treningu"
   * instead of a performance, and what keeps the ✓ / ✗ badge off a cycle nobody was judged on.
   */
  readonly verdict: CycleVerdict | null;
}

/**
 * One cycle the client has already lived, as the plot states it: what the plan FROZE onto the run's
 * own row at start, what they did on it, and the backend's verdict.
 *
 * Every number here is read off `ExerciseDraft.executions` and none is computed. The verdict
 * especially — it is the same `delivered` the grid's cell outline uses and the same one the next
 * prescription turns on, so a plot that judged for itself would badge a node green over a cycle the
 * engine refused the step for.
 */
export interface LivedCycle {
  readonly cycle: number;
  /** The FROZEN prescription on the stepped column (R2) — not the template's current cell. */
  readonly prescribed: number;
  /** What the client logged on that column, or null where they logged nothing. */
  readonly done: number | null;
  /**
   * The backend's verdict on the cycle, in full. Read, never derived.
   *
   * It is the VERDICT rather than a boolean since 15 September 2026: a `Missed` or `Cancelled` cycle
   * collapsed to `delivered: false`, and the plot then captioned it „WYKONANE" with the frozen
   * prescription standing in as the performance — a session nobody trained, drawn as one the client
   * turned up to and failed.
   */
  readonly verdict: CycleVerdict;
  /** The frozen prescription on the PAIRED column — „× 8". */
  readonly gateAsked: number | null;
  /** The band's upper end as it stood at start, or null where the rule bands nothing. */
  readonly gateCeiling: number | null;
  /** What the client logged on the paired column. */
  readonly gateDone: number | null;
}

export interface TrajectoryInput {
  readonly rules: ProgressionRules;
  /** The column the trajectory follows — the key the rule sits under. */
  readonly field: MeasurementFieldKey;
  /**
   * The axis the stepped column is WRITTEN in, which decides its arithmetic: the load priced as %1RM
   * steps a fraction by percentage points on the fraction's own grid, never by kilograms. Omitted,
   * the column's own field grid is the axis.
   */
  readonly axis?: ProgressionAxis;
  /** The authored base — what cycle one prescribes on that column, in the axis's raw unit. */
  readonly base: number;
  /** How many cycles the exercise runs for. The matrix's own column count. */
  readonly cycleCount: number;
  /** The band's floor and ceiling on the gated column, or null when the rule places no bar. */
  readonly band: TrajectoryBand | null;
  /**
   * The column the client's DELIVERY is read on, and what the row asks there — „× 12".
   *
   * The mock hard-codes repetitions, because its exercise has them. Here it is a column of the
   * exercise's own measurement contract, so a movement measured in seconds says seconds; and it is
   * the BAND's gate wherever there is one, since that is the column the rule itself is watching.
   * Null where the row prices nothing outside the stepped column, which is a plot of a bare load.
   */
  readonly gate: TrajectoryGate | null;
  readonly scenario: TrajectoryScenario;
  /**
   * The cycles the client has already lived, each at its own `cycle`. Empty or omitted on a template,
   * which is the whole run simulated and is what this function has always done.
   *
   * KEYED BY CYCLE, NEVER BY POSITION. Owner QA, 15 September 2026: an exercise whose first cycle
   * predates the plan start has its first record at cycle 2, and the positional list drew that
   * record under C1 while C2 was simulated with a green ✓, contradicting the grid beside it.
   *
   * The scenario segment never touches the real part: picking „nie dowozi" changes what the plot says
   * about the cycles from `nowCycle` on and cannot change one session that happened.
   */
  readonly lived?: readonly LivedCycle[];
  /** The absolute cycle of each offset, the exercise's lifespan. Defaults to `1..cycleCount`. */
  readonly cycles?: readonly number[];
  /**
   * The column the simulation starts at, `LiveMatrixInput.nowCycle`. A cycle before it with no lived
   * record states its plain prescription and no verdict. Omitted or null: every cycle without a
   * record is simulated, which is the template variant.
   */
  readonly nowCycle?: number | null;
}

/** The paired column and the row's own prescription on it. */
export interface TrajectoryGate {
  readonly field: MeasurementFieldKey;
  /** What the row asks there. The band's FLOOR where the rule bands that column. */
  readonly target: number;
}

/**
 * How far a banded client climbs per delivered cycle. The mock's own step, and it is a DRAWING
 * rather than a prescription: the plan states a floor and a ceiling, never the stride between them.
 */
const CLIMB_STEP = 2;

/**
 * How far a shortfall lands from what was asked, on the paired column. The mock's own two: the client
 * who fell short did two fewer repetitions, and the one who went lighter did two more.
 *
 * A DRAWING, like every other number about a client who does not exist: the engine records whether a
 * cycle was delivered and nothing about by how much. It is snapped onto the column's own progression
 * grid so a movement counted in seconds is not shown two thirds of a second short.
 */
const GATE_MISS_STEP = 2;

/**
 * The PAIRED column and what the row asks there — the second half of every label over the plot.
 *
 * The band's gate wherever there is one, because that is the column the rule itself watches. Where
 * the rule places no bar, it is the first other column the run's own row prices, in the dictionary's
 * order — which on a weight-and-repetitions movement is the repetitions the mock hard-codes, and on
 * one measured in seconds is the seconds.
 *
 * The ROW is the one the run starts from: the first that counts toward progression and prices the
 * stepped column (`sentenceBase`'s own row). A pair read off a warm-up would state a prescription the
 * rule never touches.
 */
export function trajectoryGateOf(
  exercise: Pick<ExerciseDraft, 'sets' | 'measurementFields' | 'progressionRules'>,
  axis: ProgressionAxis,
  band: TrajectoryBand | null,
): TrajectoryGate | null {
  const gated = bandedGateOf(exercise.progressionRules);
  /* Not when the band's gate IS the column being plotted: viewing the repetitions of a load rule
   * banded on them paired the repetitions with themselves, and every label read „× 8" with no load
   * (owner QA, 15 September 2026). The row's other column is the pair there. */
  if (gated !== null && band !== null && gated !== axis.field) {
    return { field: gated, target: band.floor };
  }

  const working = exercise.sets.filter((set) => countsAsWork(set.setType));
  const row = working.find((set) => axisBaseOf(set, axis) !== null) ?? working[0];
  if (row === undefined) return null;

  for (const field of exercise.measurementFields) {
    if (field === axis.field) continue;
    const valueKey = measurementValueKey(field);
    if (valueKey === null) continue;
    const value = row[valueKey];
    if (typeof value === 'number') return { field, target: value };
  }
  return null;
}

/**
 * The cycles the client has already lived, as the plot states them — the input `trajectory`'s
 * `lived` takes.
 *
 * ── Which ROW the plot follows, and why it is the record's own ──────────────────────────────
 * The run is one row's story, read off the occurrence rather than off the draft deliberately: the
 * trainer may have added, removed or re-roled a set since, and what the plot is drawing is what the
 * client was actually handed. A cycle with no counted row pricing the stepped column contributes no
 * point and stops the list — the simulation then covers it, which is honest, because there is
 * nothing to draw.
 *
 * **On a REFUSED cycle the row is the one that carries the shortfall**, and on every other cycle it
 * is the first counted one. One short row holds the whole exercise (R3), so a plot that always
 * followed the first row captioned a refused cycle with the numbers of a set that met its bar: the
 * badge said ✗ and the label under it said „100 kg × 8 / 100 kg × 8", which is a verdict contradicted
 * by the two numbers printed beside it. `shortfallFields` names the row that actually held the
 * exercise back, and that is the row the label is about.
 *
 * The lived cycles are the LEADING ones by construction (a client consumes a plan from the front),
 * so the list lines up with the plot's first columns by position. `live.verdicts` is walked in
 * insertion order, which is the ascending order `executionsFromWire` sorted the record into.
 */
export function livedCyclesOf(
  live: LiveMatrixInput | null,
  axisField: MeasurementFieldKey | null,
  gateField: MeasurementFieldKey | null,
  setCount: number,
  /** The column a frozen `prescribedCeiling` belongs to — the band's gate. The pair's, by default. */
  ceilingField: MeasurementFieldKey | null = gateField,
): LivedCycle[] {
  if (live === null || axisField === null) return [];

  const lived: LivedCycle[] = [];
  for (const [cycle, verdict] of live.verdicts) {
    /* Every cycle in the record is a point at ITS OWN cycle, `trajectory` keys them, so a hole no
     * longer slides a later entry a column left. A session the client is standing in is one of them:
     * its prescription froze at start and the simulation starts at the column after it (the next
     * cycle to perform), so it is drawn with no verdict rather than simulated. */

    const rows: LoggedCell[] = [];
    for (let setIndex = 1; setIndex <= setCount; setIndex += 1) {
      const candidate = live.logged.get(loggedCellKey(cycle, setIndex));
      if (candidate === undefined || !countsAsWork(candidate.setType)) continue;
      if (candidate.prescribed[axisField] === undefined) continue;
      rows.push(candidate);
    }
    const row =
      verdict === 'short'
        ? (rows.find((candidate) => candidate.shortfall.size > 0) ?? rows[0])
        : rows[0];
    if (row === undefined) continue;

    lived.push({
      cycle,
      prescribed: row.prescribed[axisField] ?? 0,
      done: row.actual[axisField] ?? null,
      // The BACKEND's verdict on the cycle, carried to the badge. Nothing here compares a number.
      verdict,
      gateAsked: gateField === null ? null : (row.prescribed[gateField] ?? null),
      // The band as the occurrence FROZE it, so the lived label states the range the client was
      // actually asked to climb rather than the one the trainer has since authored.
      // Only where the pair IS the banded column: a load paired with a repetitions band read „70–12 kg".
      gateCeiling: gateField === null || gateField !== ceilingField ? null : row.ceiling,
      gateDone: gateField === null ? null : (row.actual[gateField] ?? null),
    });
  }
  return lived;
}

/**
 * The run one rule produces.
 *
 * Three readings, and the third is the only one the client's result touches:
 *
 * · **No rule** — every cycle repeats the base. That is a configuration rather than a gap, and the
 *   client's result changes nothing about it.
 * · **Static** — every cycle is the base advanced by its own offset, known before the plan starts.
 *   The result changes nothing here either: the numbers were settled at authoring time.
 * · **From the result** — a delivered cycle earns the next one's jump. With a band the client climbs
 *   inside the range first and the stepped column moves only once they reach the ceiling, and the
 *   gated column drops back to its floor. Without one, delivering the whole set is the whole gate.
 *
 * A shortfall stops the run rather than reversing it: the same value comes round again. One set
 * short holds the whole exercise, which is R3's own reading and the mock's own sentence.
 */
export function trajectory(input: TrajectoryInput): TrajectoryPoint[] {
  const rule: ProgressionRule | null = ruleFor(input.rules, input.field);
  const cycles = Math.max(1, input.cycleCount);
  const fromResult = rule !== null && rule.type === 'PreviousExecution';
  const band = fromResult ? input.band : null;
  const livedByCycle = new Map((input.lived ?? []).map((entry) => [entry.cycle, entry]));
  const cycleAt = (offset: number): number => input.cycles?.[offset] ?? offset + 1;
  const nowCycle = input.nowCycle ?? null;
  /* The first offset the ENGINE answers for. The mock's three shortfall demonstrations count from it,
   * so on a live fork they start where the client will next train rather than three cycles behind. */
  let firstSimulated: number | null = null;

  const grid = steppedGrid(input.axis ?? null, input.field);
  const points: TrajectoryPoint[] = [];
  let value = grid.snap(input.base);
  let climbed = band === null ? null : band.floor;

  for (let offset = 0; offset < cycles; offset += 1) {
    /* A SESSION THAT HAPPENED. Its numbers are the record's, its verdict is the backend's, and the
     * scenario has nothing to say about it — which is the point of drawing it at all. What it still
     * does is set the ground the simulation continues from: the value the client was last given and,
     * under a band, where inside the range they were standing. */
    const cycle = cycleAt(offset);
    const real = livedByCycle.get(cycle);
    if (real !== undefined) {
      const realDelivered = deliveredOf(real.verdict);
      points.push({
        cycle: real.cycle,
        value: real.prescribed,
        climbed: real.gateAsked,
        delivered: realDelivered,
        jumped: offset > 0 && real.prescribed !== points[offset - 1]?.value,
        shortfall: null,
        // The load the client actually had on the bar, where it differed from what was asked.
        shortfallLoad:
          real.done !== null && real.done !== real.prescribed ? real.done : null,
        gateAsked: real.gateAsked,
        gateCeiling: real.gateCeiling,
        gateDone: real.gateDone,
        isReal: true,
        isSimulated: false,
        verdict: real.verdict,
      });

      value = real.prescribed;
      if (band !== null && real.gateAsked !== null) climbed = real.gateAsked;
      if (realDelivered !== true || !fromResult) continue;
      ({ value, climbed } = earnedNext({ value, climbed }, band, rule, grid));
      continue;
    }

    /* A PAST CYCLE THE RECORD HOLDS NOTHING FOR, the exercise's session there predates the plan
     * start. Nobody trained it and nobody was asked to, so it is the plain prescription: no verdict,
     * no ZROBIŁ line, and it earns nothing for the cycle after it. */
    if (nowCycle !== null && cycle < nowCycle) {
      const projected = fromResult
        ? value
        : (grid.advance(input.base, projectionRuleFor(rule), offset) ?? input.base);
      points.push({
        cycle,
        value: projected,
        climbed,
        delivered: null,
        jumped: offset > 0 && projected !== points[offset - 1]?.value,
        shortfall: null,
        shortfallLoad: null,
        gateAsked: climbed ?? input.gate?.target ?? null,
        gateCeiling: band === null || climbed === null || climbed >= band.ceiling ? null : band.ceiling,
        gateDone: null,
        isReal: false,
        isSimulated: false,
        verdict: null,
      });
      continue;
    }

    /* The mock's three ways to fall short, counted from the first SIMULATED cycle rather than from
     * cycle one: on a live fork that is the column the client trains next, and a demonstration that
     * started three cycles behind them would have run out before the plot reached them. On a
     * template the two are the same thing, which is what `max(1, …)` preserves. */
    firstSimulated ??= offset;
    const shortfall =
      offset === 0 || input.scenario === 'delivers'
        ? null
        : (SHORTFALL_BY_OFFSET[offset - Math.max(1, firstSimulated)] ?? null);
    // The base cycle is authored rather than earned, so nobody delivered it. Every cycle after it
    // carries a verdict on EVERY family — see the static branch below.
    const delivered = offset === 0 ? null : shortfall === null;

    if (!fromResult) {
      /* Static, or nothing at all: the value is the base advanced by this cycle's own offset, which
       * is what the matrix draws and what the backend materialises. The client's result changes no
       * NUMBER here — that is the whole point of the family — but it is still drawn, because the
       * trainer is comparing two families on one surface and „what if they miss" has an answer under
       * both: the plan goes on regardless. The mock draws the verdict on every family and the owner
       * confirmed it on 15 September 2026, withdrawing this app's earlier silence. */
      const projected = grid.advance(input.base, projectionRuleFor(rule), offset);
      const asked = input.gate?.target ?? null;
      points.push({
        cycle,
        value: projected ?? input.base,
        climbed: null,
        delivered,
        jumped: offset > 0 && projected !== null && projected !== points[offset - 1]?.value,
        shortfall,
        shortfallLoad: shortfallLoadOf(shortfall, projected ?? input.base, rule, grid),
        gateAsked: asked,
        gateCeiling: null,
        gateDone: gateDoneOf(delivered, shortfall, asked, null, input.gate?.field ?? null),
        isReal: false,
        isSimulated: true,
        verdict: null,
      });
      continue;
    }

    const asked = climbed ?? input.gate?.target ?? null;
    points.push({
      cycle,
      value,
      climbed,
      delivered,
      jumped: offset > 0 && value !== points[offset - 1]?.value,
      shortfall,
      shortfallLoad: shortfallLoadOf(shortfall, value, rule, grid),
      gateAsked: asked,
      gateCeiling: band === null || climbed === null || climbed >= band.ceiling ? null : band.ceiling,
      gateDone: gateDoneOf(delivered, shortfall, asked, band, input.gate?.field ?? null),
      isReal: false,
      isSimulated: true,
      verdict: null,
    });

    // What THIS cycle earns for the next one. A shortfall earns nothing and the value comes round
    // again — the rule never walks a client backwards.
    if (shortfall !== null) continue;
    ({ value, climbed } = earnedNext({ value, climbed }, band, rule, grid));
  }
  return points;
}

/**
 * Whether a cycle that HAPPENED counts as delivered.
 *
 * Only the backend's two judged readings are a yes or a no. A cycle nobody trained was never judged,
 * so it carries neither — no badge beside its node, and the caption prints the absence word rather
 * than a performance.
 */
function deliveredOf(verdict: CycleVerdict): boolean | null {
  if (verdict === 'delivered') return true;
  // A missed or skipped session is a failure on the plot as in the grid (owner, 15 September 2026).
  if (verdict === 'short' || verdict === 'missed' || verdict === 'skipped') return false;
  return null;
}

/**
 * What a DELIVERED cycle earns the next one: the jump, or one stride further up the band.
 *
 * One walk for both halves of the run — a cycle the client lived and one the scenario simulated earn
 * the next value identically, and writing it twice is how the real part and the simulated part start
 * climbing at different rates.
 */
function earnedNext(
  state: { readonly value: number; readonly climbed: number | null },
  band: TrajectoryBand | null,
  rule: ProgressionRule | null,
  grid: SteppedGrid,
): { readonly value: number; readonly climbed: number | null } {
  const { value, climbed } = state;
  const stepped = grid.advance(value, projectionRuleFor(rule), 1) ?? value;
  if (band === null || climbed === null) return { value: stepped, climbed };
  if (climbed >= band.ceiling) return { value: stepped, climbed: band.floor };
  return { value, climbed: Math.min(band.ceiling, climbed + CLIMB_STEP) };
}

/**
 * What the simulated client did on the PAIRED column, or null where nobody was asked to deliver.
 *
 * Delivered, they cleared what was asked: the whole prescription where the rule places no bar, and one
 * stride further up the band where it does — which is the cycle that earns the jump once it reaches
 * the top. Short, they landed `GATE_MISS_STEP` either side of it, on the column's own grid. Every
 * number here is the mock's drawing of a client who does not exist; see `GATE_MISS_STEP`.
 */
function gateDoneOf(
  delivered: boolean | null,
  shortfall: ShortfallKind | null,
  asked: number | null,
  band: TrajectoryBand | null,
  field: MeasurementFieldKey | null,
): number | null {
  if (delivered === null || asked === null || field === null) return null;
  if (delivered) return band === null ? asked : Math.min(band.ceiling, asked + CLIMB_STEP);
  const missed = shortfall === 'lessLoadMoreReps' ? asked + GATE_MISS_STEP : asked - GATE_MISS_STEP;
  // The column's own progression floor bounds it — repetitions stop at 1, not at zero — which is the
  // same clamp the projection itself lands on.
  return snapToProgressionGrid(missed, field);
}

/**
 * What the client's load reads as under a shortfall — the mock's own drawing of "more kilograms at
 * fewer repetitions" and its mirror.
 *
 * A DRAWING and nothing else: the plan holds no logged result for a template, and the engine records
 * only whether a cycle was delivered. It is one step either side of the prescription because that is
 * the smallest difference a trainer reads as "they went heavier" rather than as a new number.
 */
function shortfallLoadOf(
  shortfall: ShortfallKind | null,
  value: number,
  rule: ProgressionRule | null,
  grid: SteppedGrid,
): number | null {
  if (shortfall === null || shortfall === 'fewerReps') return null;
  /* The RULE's own step where there is one, and the column's own grid step where there is not — a
   * MANUAL column has no step anywhere on the sheet, and „więcej kg" drawn at the same kilograms is
   * a caption contradicting the number under it. Both readings are a drawing; neither is a fact. */
  const step = Math.abs(rule?.delta ?? 0) * grid.deltaScale || grid.step;
  if (step === 0) return null;
  return grid.snap(shortfall === 'moreLoadFewerReps' ? value + step : value - step);
}

/** The stepped column's arithmetic — how it advances, which grid it lands on, its smallest step. */
interface SteppedGrid {
  readonly advance: (base: number, rule: ProjectionRule, offset: number) => number | null;
  readonly snap: (value: number) => number;
  /** Raw units per unit of an authored delta: 1 on a field, 1/100 on %1RM (a delta is in points). */
  readonly deltaScale: number;
  /** The grid's own step where no rule names one; 0 where a column has none. */
  readonly step: number;
}

function steppedGrid(axis: ProgressionAxis | null, field: MeasurementFieldKey): SteppedGrid {
  if (axis !== null && axis.field === null) {
    return {
      advance: (base, rule, offset) => advanceOnAxis(base, axis, rule, offset),
      snap: (value) => snapToAxis(value, axis),
      deltaScale: 1 / axis.displayScale,
      step: 0,
    };
  }
  return {
    advance: (base, rule, offset) => advanceFieldValue(base, field, rule, offset),
    snap: (value) => snapToProgressionGrid(value, field),
    deltaScale: 1,
    step: measurementFieldDefinition(field).progressionStep ?? 0,
  };
}

/* ── What the chart SAYS, over what it draws ───────────────────────────────────────────────── */

/** One point's two lines, as the mock writes them over each node. */
export interface TrajectoryLabel {
  /** „24 kg × 10–12" — what the plan asks for in this cycle. */
  readonly prescribed: string;
  /**
   * „ZROBIŁ · GÓRA ✓" over „24 kg × 12", or null on a cycle nobody was asked to deliver.
   *
   * THE VERDICT RIDES ON THE CAPTION, NEVER INSTEAD OF THE NUMBERS. Until 15 September 2026 this
   * line was the word „dowieziony" — the plot printed „ZADANE 3 / ZROBIŁ dowieziony", which states a
   * verdict on a cycle whose two numbers the trainer came to the chart to read. The mock's own line
   * is the same shape as the one above it: a value, its pair, and the verdict as a suffix.
   */
  readonly done: {
    /**
     * The label word — „ZROBIŁ", or „ZROBIŁ · GÓRA ✓" on the cycle that cleared the band's top. It
     * is the part the caption's own `text-transform` uppercases.
     */
    readonly caption: string;
    /**
     * HOW the cycle fell short, in the mock's own shorthand — „↓powt.", „↑kg ↓powt." — or null where
     * it did not. It rides beside the word rather than inside it because the mock keeps it in its
     * own case: „ZROBIŁ ↓powt.", never „ZROBIŁ ↓POWT.".
     */
    readonly mark: string | null;
    readonly text: string;
    /**
     * `neutral` is the third since 15 September 2026 and it is not a shade of „missed": a cycle
     * nobody trained was never judged, so the caption states the absence and the line carries no
     * verdict colour at all.
     */
    readonly tone: 'delivered' | 'missed' | 'neutral';
  } | null;
}

/** Everything the preview WORDS: one pair of lines per node, and the sentence under the plot. */
export interface TrajectoryNarration {
  readonly labels: readonly TrajectoryLabel[];
  readonly summary: string;
  /**
   * The whole run as one sentence, for the plot's accessible NAME.
   *
   * `role="img"` swallows its subtree, so the name is the only reading a screen reader gets — and it
   * was built from the raw `points[].value` alone, which stated five numbers and none of the four
   * things the sighted reader takes off the same plot: which cycles happened, what was asked, what
   * was done and how each one was judged. It is built from the LABELS instead, so the two readings
   * of this plot can never state different numbers.
   */
  readonly runText: string;
}

export interface NarrationInput {
  readonly points: readonly TrajectoryPoint[];
  /** The STEPPED column, where its number sits in a label's row is the row formatter's call. */
  readonly field: MeasurementFieldKey;
  /**
   * One value on the STEPPED column, formatted with its unit — „102,5 kg".
   *
   * A formatter rather than the formatted run, because the labels need it over numbers no point
   * carries as its value: what a client who went heavier put on the bar under a shortfall. The
   * component that owns the axis passes it in and prints the same run through it, so the plot's
   * labels and its accessible name can never state one number two ways.
   */
  readonly formatValue: (raw: number) => string;
  /** The band the run was projected against, or null where the rule places no bar. */
  readonly band: TrajectoryBand | null;
  /** The rule on the viewed column, or null when the column carries none. */
  readonly rule: ProgressionRule | null;
  /** The step as the sentence above prints it, so the two never state it differently. */
  readonly stepText: string;
  /** The i18n key naming the stepped column — the sentence's own noun, never a column header. */
  readonly columnKey: string;
  /**
   * The PAIRED column, as a FIELD rather than a resolved noun.
   *
   * Two readings hang off it and both need the field itself: the labels print a number in that
   * column's own notation (`prescriptionText`), and the summary needs two different words for it —
   * the unit its two numbers are counted in, and the subject of the clause that sends it back to the
   * floor. The summary's two are `ruleSentence.ts`'s and are only ever read under a band, which is
   * the case where this field IS the band's gate.
   */
  readonly gateField: MeasurementFieldKey | null;
  /** The cycle heads the matrix prints, so the spoken run names the same columns the grid does. */
  readonly cycleLabels: readonly string[];
  readonly t: Translate;
  readonly locale: string;
}

/** Resolve one locale key, with interpolation. Handed in, never imported. */
type Translate = (key: string, values?: Record<string, string | number>) => string;

const PREVIEW = 'web:planBuilder.progression.sheet.preview';
const CHART = 'web:planBuilder.progression.sheet.chart';
/** The CELL's own absence words. One vocabulary for „bez treningu", whichever box prints it. */
const CELL = 'web:planBuilder.progression.cell.lived';

/**
 * The mock's own narration — the two lines over each node and the sentence under the plot.
 *
 * It lives here rather than in the component for the reason every decision on this surface does:
 * the words state what the RULE will do, which is the same claim the sentence at the top of the
 * sheet makes, and two places to word it is two places for them to disagree. The trainer reading
 * „ciężar rośnie o +2,5 kg" above and a summary saying something else one box down is the defect
 * this shape prevents.
 *
 * The mock's own narration carries a trailing clause about cycles C2–C4 illustrating three ways to
 * fall short. It is not here: it describes the mock's fixed three-shortfall demonstration, and this
 * surface runs one scenario at a time on the trainer's own numbers.
 */
export function trajectoryNarration(input: NarrationInput): TrajectoryNarration {
  const { points, field, formatValue, band, rule, columnKey, gateField, cycleLabels, t, locale } =
    input;
  const fromResult = rule !== null && rule.type === 'PreviousExecution';
  const column = t(columnKey);
  const gate = gateField === null ? '' : t(gateSubjectKey(gateField));
  const valueTexts = points.map((point) => formatValue(point.value));
  const baseText = valueTexts[0] ?? '';

  /* „24 kg" or „24 kg × 10–12", the stepped column and its pair, worded by the SAME row formatter the
   * grid's cells use. The label was `value + pair` until 15 September 2026, which put the stepped
   * column first whatever it was: on the Powt. chip the plot read „ZADANE 10 60 kg" while the grid two
   * boxes up read „60 kg × 10" for the same set. The stepped number keeps the plot's own formatting
   * (its axis scale); where it sits in the row is `setRowText`'s. */
  const fields = [field, ...(gateField === null ? [] : [gateField])];
  const withPair = (valueText: string, asked: number | null, ceiling: number | null): string =>
    setRowText(
      { values: asked === null || gateField === null ? {} : { [gateField]: asked }, ceiling, amrap: false },
      { fields, ceilingField: gateField, formatted: { [field]: valueText }, t, locale },
    ) ?? valueText;

  const labels = points.map<TrajectoryLabel>((point, index) => {
    const valueText = valueTexts[index] ?? '';
    /* THE MOCK'S OWN THREE CONDITIONS for the pair, and they are not symmetrical: always under a
     * band, on any cycle that fell short, and on every earned cycle of an execution-gated rule. A
     * DELIVERED static cycle states its number alone — the pair says what had to be done to earn the
     * next one, and a static rule earns nothing. `miss || (isResult && !bindOn && i > 0)`. */
    const showsPair =
      /* Owner QA, 15 September 2026: off the load the stepped number is not the head of its row, and
       * a bare „ZADANE 10" says ten of nothing. The mock's asymmetry holds on the load alone. */
      field !== 'weight' ||
      point.isReal ||
      band !== null ||
      point.delivered === false ||
      (fromResult && point.delivered !== null);
    const prescribed = showsPair
      ? withPair(valueText, point.gateAsked, point.gateCeiling)
      : valueText;
    /* A SESSION NOBODY TRAINED. It happened in the sense that the cycle is behind the client and it
     * was never judged, so there is no performance to print and no verdict to badge: the caption is
     * the CELL's own absence word and the line under it is empty. Captioning it „WYKONANE" and
     * repeating the frozen prescription as the performance — which is what it did until 15 September
     * 2026 — tells a trainer their client turned up and matched the plan exactly. */
    if (point.isReal && point.delivered === null) {
      return {
        prescribed,
        done: {
          caption: t(`${CELL}.${absenceKindOf(point.verdict)}`),
          mark: null,
          text: '',
          tone: 'neutral',
        },
      };
    }
    if (point.delivered === null) return { prescribed, done: null };
    /* A SESSION THE CLIENT DID NOT DO. The ✗ badge is on the node; the caption says it in words and
     * no performance number stands under it, because there is none. */
    if (point.isReal && (point.verdict === 'missed' || point.verdict === 'skipped')) {
      return {
        prescribed,
        done: { caption: t(`${CHART}.captionNotDone`), mark: null, text: '', tone: 'missed' },
      };
    }

    const doneText = withPair(
      formatValue(point.shortfallLoad ?? point.value),
      point.gateDone,
      null,
    );
    /* A SESSION THAT HAPPENED says so. „WYKONANE" rather than „ZROBIŁ", because the second is the
     * simulated client — the one the scenario segment invents — and a trainer reading the plot has
     * to be able to tell the two apart at a glance. The word is the plan's proposal and the owner's
     * to confirm; the distinction it carries is not. It also drops the shortfall shorthand
     * („↓powt."), which is a caption for one of the mock's three DEMONSTRATIONS and describes
     * nothing about a real session. */
    if (point.isReal) {
      return {
        prescribed,
        done: {
          caption: t(`${CHART}.captionLogged`),
          mark: null,
          text: doneText,
          tone: point.delivered ? 'delivered' : 'missed',
        },
      };
    }
    if (point.delivered) {
      // „ · GÓRA ✓" only where the delivery actually REACHED the bar: it is the cycle that earns the
      // jump, and saying it on every delivered cycle of a band would promise one four times over.
      const reachedTop =
        band !== null && point.gateDone !== null && point.gateDone >= band.ceiling;
      return {
        prescribed,
        done: {
          caption: t(`${CHART}.caption${reachedTop ? 'DoneTop' : 'Done'}`),
          mark: null,
          text: doneText,
          tone: 'delivered',
        },
      };
    }
    return {
      prescribed,
      done: {
        caption: t(`${CHART}.captionDone`),
        mark: point.shortfall === null ? null : t(`${CHART}.shortfall.${point.shortfall}`),
        text: doneText,
        tone: 'missed',
      },
    };
  });

  return {
    labels,
    summary: summaryOf({ ...input, valueTexts, column, gate, baseText }),
    runText: labels
      .map((label, index) => {
        const point = points[index];
        const cycle = cycleLabels[index] ?? String(point?.cycle ?? index + 1);
        if (label.done === null) return t(`${CHART}.spoken.plain`, { cycle, prescribed: label.prescribed });
        /* A CYCLE NOBODY JUDGED IS ITS OWN SENTENCE, the same way the cell's is
         * (`cell.lived.ariaAbsent`). It was read out through the lived one — „C1, wykonane: zadane
         * 100 kg × 8–12, zrobione bez treningu" for a missed cycle, and „zrobione —" for one the
         * backend declined to judge — which states a performance and then names one that never
         * happened. The absence word REPLACES the performance clause rather than standing inside it,
         * and the verdict suffix goes with it: there is no verdict to say. */
        if (label.done.text === '') {
          return t(`${CHART}.spoken.absent`, {
            cycle,
            prescribed: label.prescribed,
            absence: t(`${CELL}.absent.${absenceKindOf(point?.verdict ?? null)}`),
          });
        }
        return t(`${CHART}.spoken.${point?.isReal === true ? 'lived' : 'projected'}`, {
          cycle,
          prescribed: label.prescribed,
          done: label.done.text,
          verdict: t(`${CHART}.spoken.verdict.${label.done.tone}`),
        });
      })
      .join('; '),
  };
}

function summaryOf(
  input: NarrationInput & {
    readonly valueTexts: readonly string[];
    readonly column: string;
    readonly gate: string;
    readonly baseText: string;
  },
): string {
  const { valueTexts, band, rule, stepText, gateField, t, locale, column, gate, baseText } = input;
  if (rule === null) return t(`${PREVIEW}.summary.none`, { base: baseText });
  if (rule.type === 'Static') {
    return t(`${PREVIEW}.summary.static`, { run: valueTexts.join(' → ') });
  }
  if (band === null || gateField === null) {
    return t(`${PREVIEW}.summary.result`, { base: baseText, step: stepText });
  }
  const floor = formatNumber(band.floor, locale);
  const ceiling = formatNumber(band.ceiling, locale);
  return t(`${PREVIEW}.summary.banded`, {
    base: baseText,
    // „8–12 powtórzeń", counted in whatever the gated column counts in — see `bandCountText`.
    band: t(`${PREVIEW}.summary.bandPhrase`, {
      floor,
      ceiling,
      unit: bandCountText(gateField, band.ceiling, t),
    }),
    floor,
    ceiling,
    gate,
    column,
    step: stepText,
  });
}

/* ── The run the preview draws, and the axis it is drawn against ───────────────────────────── */

export interface TrajectoryRunInput {
  /** The exercise under the STAGED rule map. */
  readonly staged: ExerciseDraft;
  /** The column the run follows. */
  readonly field: MeasurementFieldKey;
  /** The lived record, or null on a template. */
  readonly live: LiveMatrixInput | null;
  /** The absolute cycle under each column, the exercise's lifespan. */
  readonly cycles: readonly number[];
  readonly scenario: TrajectoryScenario;
}

/** One column's run, with the pieces the narration words it from. */
export interface TrajectoryRun {
  readonly points: readonly TrajectoryPoint[];
  readonly axis: ProgressionAxis;
  readonly rule: ProgressionRule | null;
  /** The band as the PLOT reads it: only where its gate is the pair being printed. */
  readonly band: TrajectoryBand | null;
  readonly gate: TrajectoryGate | null;
}

/**
 * The run one column of the staged exercise draws over its lifespan, lived cycles included, or null
 * where no working row prices that column and there is nothing to draw.
 */
export function trajectoryRun(input: TrajectoryRunInput): TrajectoryRun | null {
  const { staged, field, live, cycles, scenario } = input;
  const axis = axisForField(field, staged.prescriptionMode);
  if (axis === null) return null;
  const base = sentenceBase(staged.sets, axis);
  if (base === null) return null;

  const band = sentenceBand(staged);
  const gate = trajectoryGateOf(staged, axis, band);
  /* The band belongs to the plot only when its gate is the pair being printed. Viewing the
   * repetitions of a load rule banded on them pairs them with the load, and the band then printed
   * „70–12 kg" and captioned „GÓRA ✓" on a column that has no top. */
  const plotBand = gate !== null && gate.field === bandedGateOf(staged.progressionRules) ? band : null;
  const lived = livedCyclesOf(live, axis.field, gate?.field ?? null, staged.sets.length, bandColumnOf(staged));
  return {
    points: trajectory({
      rules: staged.progressionRules,
      field,
      axis,
      base,
      cycleCount: cycles.length,
      band: plotBand,
      gate,
      scenario,
      lived,
      cycles,
      nowCycle: live?.nowCycle ?? null,
    }),
    axis,
    rule: ruleFor(staged.progressionRules, field),
    band: plotBand,
    gate,
  };
}

/**
 * The column the plot's Y AXIS measures, decided per EXERCISE and never per chip (owner, 15 September
 * 2026): the load wherever the exercise measures one, the repetitions where it has no load, and
 * otherwise its one progression column (time, distance, rounds). Null where that last reading is
 * ambiguous, two such columns, and the plot then measures the column being viewed.
 */
export function trajectoryValueAxisField(
  measurementFields: readonly MeasurementFieldKey[],
): MeasurementFieldKey | null {
  const columns = availableAxisFields(measurementFields);
  if (columns.includes('weight')) return 'weight';
  if (columns.includes('reps')) return 'reps';
  const progressing = columns.filter((field) => measurementFieldDefinition(field).decidesProgression);
  return progressing.length === 1 ? (progressing[0] ?? null) : null;
}

/** The plot's value axis: where every node stands, and the lines it is read against. */
export interface TrajectoryValueAxis {
  /** The column it measures, and how that column is scaled and denominated (`percent` on %1RM). */
  readonly field: MeasurementFieldKey;
  readonly kind: ProgressionAxisKind;
  /** Each point's height, in displayed units, parallel to the viewed run's points. */
  readonly values: readonly number[];
  readonly ticks: readonly number[];
  /** The bottom of the axis, its first tick. */
  readonly min: number;
  /** The top of the axis, its last tick. */
  readonly max: number;
}

/**
 * The axis the preview plots against, and each point's value on it.
 *
 * ── SCALED TO THE DATA, NOT FROM ZERO ───────────────────────────────────────────────────────
 * Owner, 15 September 2026: an axis from 0 drew a 140 to 147,5 kg run on 50 kg ticks as a flat line
 * nobody could read a step off. The axis spans the values plotted, a tick below and above, and every
 * tick is labelled, so the scale is explicit rather than guessed at.
 *
 * Where the axis column is not the viewed one, its OWN run is projected (its rule, the same scenario,
 * the same lived record) so a node's height is always a number the exercise really holds on that
 * column. Where that column has nothing to draw, the viewed run is the axis.
 */
export function trajectoryValueAxis(
  input: TrajectoryRunInput,
  viewed: TrajectoryRun,
): TrajectoryValueAxis {
  const axisField = trajectoryValueAxisField(input.staged.measurementFields) ?? input.field;
  const own = axisField === input.field ? viewed : trajectoryRun({ ...input, field: axisField });
  const run = own === null || own.points.length !== viewed.points.length ? viewed : own;
  const field = run === viewed ? input.field : axisField;
  const values = run.points.map((point) => point.value * run.axis.displayScale);
  /* The axis is sized over BOTH scenarios, so pressing „nie dowozi" cannot move a lived node: a
   * session that happened stays where it was drawn while the what-if changes around it. */
  const other = trajectoryRun({
    ...input,
    field,
    scenario: input.scenario === 'delivers' ? 'fallsShort' : 'delivers',
  });
  const otherValues = (other?.points ?? []).map((point) => point.value * run.axis.displayScale);
  const gridStep =
    run.axis.kind === 'percent' ? 1 : (measurementFieldDefinition(field).progressionStep ?? 1);
  const plotted = [...values, ...otherValues];
  const ticks = valueAxisTicks(Math.min(...plotted), Math.max(...plotted), gridStep);
  return {
    field,
    kind: run.axis.kind,
    values,
    ticks,
    min: ticks[0] ?? 0,
    max: ticks[ticks.length - 1] ?? 0,
  };
}

/** The mantissas an axis step may take, per decade. */
const NICE_STEPS = [1, 2, 2.5, 5] as const;
const MAX_TICKS = 5;
const MIN_TICKS = 3;
const EPSILON = 1e-9;

/**
 * Ticks over the plotted range `min` to `max`, three to five of them, on the column's own grid.
 *
 * The axis ends on the first tick strictly below `min` and the first strictly above `max`, so no
 * point sits on the plot's edge, and it never goes under 0. The step is the smallest „nice" number
 * (1, 2, 2,5 or 5 in any decade) that is a whole multiple of the column's progression step and fits
 * that span in at most five ticks: the load steps in 2,5 / 5 / 10 / 25 kg, the repetitions in
 * 1 / 2 / 5, so no tick states a value the column cannot hold.
 */
export function valueAxisTicks(min: number, max: number, gridStep: number): number[] {
  const onGrid = (candidate: number) => {
    const ratio = candidate / gridStep;
    return candidate >= gridStep - EPSILON && Math.abs(ratio - Math.round(ratio)) < EPSILON;
  };
  const firstDecade = Math.floor(Math.log10(gridStep));
  const candidates = Array.from({ length: 12 }, (_unused, offset) =>
    NICE_STEPS.map((mantissa) => mantissa * 10 ** (firstDecade + offset)),
  )
    .flat()
    .filter(onGrid);
  const span = (step: number) => {
    const low = Math.max(0, Math.ceil(min / step - EPSILON) - 1);
    const high = Math.max(Math.floor(max / step + EPSILON) + 1, low + MIN_TICKS - 1);
    return { low, high };
  };
  const step =
    candidates.find((candidate) => {
      const { low, high } = span(candidate);
      return high - low + 1 <= MAX_TICKS;
    }) ??
    candidates[candidates.length - 1] ??
    gridStep;
  const { low, high } = span(step);
  return Array.from({ length: high - low + 1 }, (_unused, index) =>
    Number(((low + index) * step).toFixed(6)),
  );
}
